import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { BoardPack, BudgetVersion, FpaService } from '../../../services/fpa.service';
import { StatementTable } from '../../../services/financialStatement.service';
import StatementTableView from '../StatementTableView';
import SearchableSelect from '../../common/SearchableSelect';
import { formatMonthYear, toLocalIsoDate } from '../../../utils/dateUtils';

const lastMonthEnd = (): string => { const d = new Date(); d.setDate(0); return toLocalIsoDate(d); };

const BoardPackPage: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.BUDGET_MANAGE);
    const [packs, setPacks] = useState<BoardPack[]>([]);
    const [versions, setVersions] = useState<BudgetVersion[]>([]);
    const [period, setPeriod] = useState(lastMonthEnd());
    const [versionId, setVersionId] = useState('');
    const [current, setCurrent] = useState<BoardPack | null>(null);
    const [table, setTable] = useState<StatementTable | null>(null);
    const [busy, setBusy] = useState(false);
    const [confirming, setConfirming] = useState<'finalize' | 'delete' | null>(null);

    const loadPacks = useCallback(async () => {
        try { setPacks(await FpaService.listBoardPacks()); } catch (err) { toast.showApiError(err, 'Failed to load board packs'); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadPacks();
        FpaService.listVersions().then(v => setVersions(v.filter(x => x.status !== 'DRAFT'))).catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const open = async (p: BoardPack) => {
        try { setCurrent(await FpaService.getBoardPack(p.id)); setTable(await FpaService.getBoardPackTable(p.id)); }
        catch (err) { toast.showApiError(err, 'Failed to open the board pack'); }
    };

    const create = async () => {
        try { setBusy(true); const p = await FpaService.createBoardPack(period, versionId || undefined); toast.showSuccess('Board pack created'); await loadPacks(); await open(p); }
        catch (err) { toast.showApiError(err, 'Could not create the board pack'); }
        finally { setBusy(false); }
    };

    const setCommentary = (key: string, v: string) =>
        current && setCurrent({ ...current, sections: current.sections.map(s => s.key === key ? { ...s, commentary: v } : s) });

    const save = async () => {
        if (!current) return;
        try { setBusy(true); const p = await FpaService.saveBoardPack(current.id, current.budgetVersionId ?? undefined, current.sections); toast.showSuccess('Saved'); await open(p); }
        catch (err) { toast.showApiError(err, 'Could not save'); }
        finally { setBusy(false); }
    };

    const finalize = async () => {
        if (!current) return;
        setConfirming(null);
        try { setBusy(true); const p = await FpaService.finalizeBoardPack(current.id); toast.showSuccess('Board pack finalized'); await loadPacks(); await open(p); }
        catch (err) { toast.showApiError(err, 'Could not finalize'); }
        finally { setBusy(false); }
    };

    const remove = async () => {
        if (!current) return;
        setConfirming(null);
        try { await FpaService.deleteBoardPack(current.id); setCurrent(null); setTable(null); await loadPacks(); }
        catch (err) { toast.showApiError(err, 'Could not delete'); }
    };

    const draft = current?.status === 'DRAFT' && canManage;

    return (
        <Box data-help-page="accounting.boardPack.page" sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Board Pack</Typography>
            {canManage && (
                <Box data-help-section="accounting.boardPack.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 3 }}>
                    <TextField data-help="accounting.boardPack.period" label="Period end" type="date" size="small" value={period} onChange={e => setPeriod(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                    <Box sx={{ flex: '1 1 240px', maxWidth: 320 }}>
                        <SearchableSelect data-help="accounting.boardPack.version" options={versions} value={versionId} onChange={setVersionId} getOptionLabel={v => `${v.name} (${v.status})`} label="Budget version (optional)" size="small" />
                    </Box>
                    <Button data-help="accounting.boardPack.new" variant="contained" onClick={create} disabled={busy}>New board pack</Button>
                </Box>
            )}
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table data-help-section="accounting.boardPack.table" size="small">
                    <TableHead><TableRow><TableCell data-help="accounting.boardPack.col.period">Period</TableCell><TableCell data-help="accounting.boardPack.col.status">Status</TableCell></TableRow></TableHead>
                    <TableBody>
                        {packs.map(p => (
                            <TableRow key={p.id} hover selected={current?.id === p.id} sx={{ cursor: 'pointer' }} onClick={() => open(p)}>
                                <TableCell>{formatMonthYear(p.period)}</TableCell>
                                <TableCell><Chip size="small" label={p.status} color={p.status === 'FINAL' ? 'success' : 'warning'} /></TableCell>
                            </TableRow>
                        ))}
                        {packs.length === 0 && <TableRow><TableCell colSpan={2}><Typography variant="body2" color="text.secondary">No board packs yet.</Typography></TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>

            {current && (
                <Box>
                    <Box data-help-section="accounting.boardPack.toolbar" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Button data-help="accounting.boardPack.action.pdf" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => FpaService.downloadBoardPackPdf(current.id, current.period).catch(err => toast.showApiError(err, 'Download failed'))}>PDF</Button>
                        {draft && <Button data-help="accounting.boardPack.action.save" variant="contained" disabled={busy} onClick={save}>Save commentary</Button>}
                        {draft && <Button data-help="accounting.boardPack.action.finalize" color="success" variant="outlined" disabled={busy} onClick={() => setConfirming('finalize')}>Finalize</Button>}
                        {draft && <Button data-help="accounting.boardPack.action.delete" color="error" variant="outlined" disabled={busy} onClick={() => setConfirming('delete')}>Delete draft</Button>}
                    </Box>
                    <Stack spacing={1.5} sx={{ mb: 3 }}>
                        {current.sections.map(s => (
                            <TextField data-help="accounting.boardPack.commentary" key={s.key} label={`${s.title ?? s.key} - commentary`} multiline minRows={2} size="small" value={s.commentary ?? ''}
                                disabled={!draft} onChange={e => setCommentary(s.key, e.target.value)} inputProps={{ maxLength: 4000 }} />
                        ))}
                    </Stack>
                    {table && <StatementTableView table={table} />}
                </Box>
            )}
            <Dialog data-help-page="accounting.boardPack.confirm.page" open={confirming !== null} onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{confirming === 'finalize' ? 'Finalize this board pack?' : 'Delete this draft board pack?'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirming === 'finalize'
                            ? 'Finalizing freezes the commentary (including the variance notes) and stops the budget version it uses from being reopened. The figures themselves are still read live.'
                            : 'The draft and its commentary will be removed.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.boardPack.confirm.cancel" onClick={() => setConfirming(null)}>Cancel</Button>
                    <Button data-help="accounting.boardPack.confirm.submit" variant="contained" color={confirming === 'finalize' ? 'success' : 'error'}
                        onClick={() => (confirming === 'finalize' ? finalize() : remove())}>
                        {confirming === 'finalize' ? 'Finalize' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BoardPackPage;
